import api from './api';

/**
 * PRX-145 (WP-20) — kidney-function labs on Patient 360, and the
 * Cockcroft-Gault CrCl, CKD stage and renal-dosing flag the backend computes
 * from them (`backend-v2/src/patient/utils/creatinine-clearance.util.ts`, the
 * one copy of the formula — nothing is computed here).
 */
export type LabSex = 'F' | 'M';

export type KidneyFunctionMissingField = 'dateOfBirth' | 'sex' | 'serumCreatinine' | 'weight' | 'drawnOn';

export type KidneyFunctionResult =
  | {
      status: 'computed';
      crclMlPerMin: number;
      ageYears: number;
      ckdStage: 'G1' | 'G2' | 'G3a' | 'G3b' | 'G4' | 'G5';
      ckdStageLabel: string;
      renalDosing: boolean;
    }
  | { status: 'cannot_compute'; missing: KidneyFunctionMissingField[] }
  | { status: 'not_applicable'; reason: 'under_18' | 'drawn_before_birth' | 'implausible_date_of_birth' };

export interface PatientLabValue {
  id: string;
  serumCreatinineUmolL: number;
  weightKg: number;
  heightCm: number | null;
  sex: LabSex | null;
  /** 'YYYY-MM-DD'. */
  drawnOn: string;
  enteredByUserName: string | null;
  /** ISO instant. */
  enteredAt: string;
  olderThan12Months: boolean;
  kidneyFunction: KidneyFunctionResult;
}

export interface PatientKidneyFunction {
  latest: PatientLabValue | null;
  renalDosing: boolean;
  history: PatientLabValue[];
}

export interface RecordLabValuesPayload {
  serumCreatinineUmolL: number;
  weightKg: number;
  heightCm?: number;
  sex?: LabSex;
  /** 'YYYY-MM-DD', never in the future. */
  drawnOn: string;
}

const labValuesPath = (pharmacyId: string, patientId: string) =>
  `/api/patients/${encodeURIComponent(pharmacyId)}/${encodeURIComponent(patientId)}/lab-values`;

export const patientLabValuesService = {
  async list(pharmacyId: string, patientId: string): Promise<PatientKidneyFunction> {
    const response = await api.get<PatientKidneyFunction>(labValuesPath(pharmacyId, patientId));
    return response.data;
  },

  async record(pharmacyId: string, patientId: string, payload: RecordLabValuesPayload): Promise<PatientLabValue> {
    const response = await api.post<PatientLabValue>(labValuesPath(pharmacyId, patientId), payload);
    return response.data;
  },
};
